"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useTransition } from "react";
import { CircleHelp, Crown } from "lucide-react";
import { toast } from "sonner";
import { getStorySpotlightViewerState, placeSpotlight } from "@/lib/actions/spotlight-actions";
import type { StorySpotlightState } from "@/lib/spotlights/queries";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ARTICLE_SLUGS, articlePath } from "@/lib/articles/slugs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ArticleLink } from "@/components/articles/article-link";
export function SpotlightControl(t0) {
  const $ = _c(36);
  const {
    storyId,
    initialState,
    compact: t1,
    loadViewerState: t2
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const loadViewerState = t2 === undefined ? false : t2;
  const [state, setState] = useState(initialState);
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  let t3;
  let t4;
  if ($[0] !== initialState.isEnabled || $[1] !== loadViewerState || $[2] !== storyId) {
    t3 = () => {
      if (!loadViewerState || !initialState.isEnabled) {
        return;
      }
      let isCurrent = true;
      getStorySpotlightViewerState(storyId).then(viewerState => {
        if (isCurrent) {
          setState(viewerState);
        }
      });
      return () => {
        isCurrent = false;
      };
    };
    t4 = [initialState.isEnabled, loadViewerState, storyId];
    $[0] = initialState.isEnabled;
    $[1] = loadViewerState;
    $[2] = storyId;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  if (!state.isEnabled || !state.canReceiveCrowns) {
    return null;
  }
  const canSpotlight = state.isAuthenticated && state.hasAccess && !state.isOwnStory && state.isQualified && !state.hasSpotlightedThisWeek && state.remainingAllowance > 0;
  let t5;
  if ($[5] !== storyId) {
    t5 = () => {
      startTransition(async () => {
        const result = await placeSpotlight(storyId);
        if ("error" in result) {
          toast.error(result.error);
          return;
        }
        setState(current => ({
          ...current,
          count28Days: current.count28Days + 1,
          hasSpotlightedThisWeek: true,
          remainingAllowance: result.remainingAllowance,
          weeklyAllowance: result.weeklyAllowance,
          totalXp: result.totalXp,
          progressMessage: "You Crowned this story this week."
        }));
        setIsOpen(false);
        toast.success("Story Crowned \u2014 thanks for recommending it!");
      });
    };
    $[5] = storyId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleConfirm = t5;
  const t6 = compact ? "p-4" : "p-5";
  let t7;
  if ($[7] !== t6) {
    t7 = cn("rounded-xl border border-primary/20 bg-primary/5", t6);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Crown className="size-4 text-amber-400" aria-hidden="true" />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = articlePath(ARTICLE_SLUGS.WHAT_ARE_READER_CROWNS);
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <HoverCardTrigger asChild={true}><ArticleLink href={t9} aria-label="Learn about Crowns" className="-translate-y-1 rounded-full text-amber-400 transition-colors hover:text-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><CircleHelp className="size-3.5" aria-hidden="true" /></ArticleLink></HoverCardTrigger>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex items-center gap-2 font-heading font-bold text-foreground">{t8}Recommend this story<HoverCard openDelay={100} closeDelay={200}>{t10}<HoverCardContent className="w-auto px-3 py-2 text-sm" side="top">Learn about{" "}<ArticleLink href={articlePath(ARTICLE_SLUGS.WHAT_ARE_READER_CROWNS)} className="font-semibold text-amber-500 underline decoration-amber-500/60 underline-offset-2 transition-colors hover:text-amber-400 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Crowns</ArticleLink></HoverCardContent></HoverCard></div>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== state.progressMessage) {
    t12 = <p className="mt-1 text-sm text-muted-foreground">{state.progressMessage}</p>;
    $[13] = state.progressMessage;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== state.hasAccess || $[16] !== state.remainingAllowance || $[17] !== state.weeklyAllowance) {
    t13 = state.hasAccess && <p className="mt-1 text-xs font-medium text-primary">{state.remainingAllowance} of {state.weeklyAllowance} left this week</p>;
    $[15] = state.hasAccess;
    $[16] = state.remainingAllowance;
    $[17] = state.weeklyAllowance;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <div className="min-w-0">{t11}{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== canSpotlight || $[23] !== handleConfirm || $[24] !== isOpen || $[25] !== isPending || $[26] !== state.hasSpotlightedThisWeek || $[27] !== state.progressMessage || $[28] !== state.remainingAllowance) {
    t15 = canSpotlight ? <Dialog open={isOpen} onOpenChange={setIsOpen}><DialogTrigger asChild={true}><Button className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400"><Crown className="size-4" /> Crown this story</Button></DialogTrigger><DialogContent><DialogHeader><DialogTitle>Crown this story?</DialogTitle><DialogDescription>You're recommending it to other readers. You have {state.remainingAllowance} left this week.</DialogDescription></DialogHeader><DialogFooter><DialogClose asChild={true}><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleConfirm} disabled={isPending} className="bg-amber-500 text-slate-950 hover:bg-amber-400">{isPending ? "Crowning\u2026" : "Crown story"}</Button></DialogFooter></DialogContent></Dialog> : <Tooltip><TooltipTrigger asChild={true}><span tabIndex={0} className="inline-flex rounded-full"><Button disabled={true} variant="outline" className="rounded-full"><Crown className="size-4" />{state.hasSpotlightedThisWeek ? "Crowned this week" : "Crown this story"}</Button></span></TooltipTrigger><TooltipContent className="max-w-72">{state.progressMessage}</TooltipContent></Tooltip>;
    $[22] = canSpotlight;
    $[23] = handleConfirm;
    $[24] = isOpen;
    $[25] = isPending;
    $[26] = state.hasSpotlightedThisWeek;
    $[27] = state.progressMessage;
    $[28] = state.remainingAllowance;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t14 || $[31] !== t15) {
    t16 = <div className="flex flex-wrap items-center justify-between gap-4">{t14}{t15}</div>;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16 || $[34] !== t7) {
    t17 = <div className={t7} data-testid="spotlight-control">{t16}</div>;
    $[33] = t16;
    $[34] = t7;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  return t17;
}